import React, { useState, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames'
import { uniqueId, isEmpty } from 'lodash'

// Components
import { Text } from './..'

import styles from './Slider.module.scss'

// Marker Styles
const markerWidth = styles.markerWidth
const markerTopUpDirection = styles.markerTopUpDirection
const markerTopDownDirection = styles.markerTopDownDirection

// Marker Content Styles
const markerContentTopUpDirectionSingleLine =
  styles.markerContentTopUpDirectionSingle
const markerContentTopUpDirectionDoubleLine =
  styles.markerContentTopUpDirectionDouble
const markerContentTopDownDirection = styles.markerContentTopDownDirection

const propTypes = {
  ariaLabel: PropTypes.string,
  caption: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  className: PropTypes.string,
  defaultValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  id: PropTypes.string,
  markers: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.number.isRequired,
      direction: PropTypes.string.isRequired,
      content: PropTypes.object.isRequired,
      multiline: PropTypes.bool,
    })
  ),
  max: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  maxPhrase: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  min: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  minPhrase: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  name: PropTypes.string,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  onMouseDown: PropTypes.func,
  onMouseUp: PropTypes.func,
  onTouchEnd: PropTypes.func,
  onTouchStart: PropTypes.func,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
}

const defaultProps = {
  ariaLabel: 'slider values',
  max: '100',
  min: '0',
}

const defaultCaptionPropTypes = {
  minPhrase: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  maxPhrase: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
}

const CaptionText = props => (
  <Text size='xxs' weight='bold' color='heather' element='span' {...props} />
)

const DefaultCaption = ({ minPhrase, maxPhrase }) => (
  <div className={styles.caption}>
    <CaptionText>{minPhrase}</CaptionText>
    <CaptionText>{maxPhrase}</CaptionText>
  </div>
)

const Slider = ({
  ariaLabel,
  caption,
  className,
  defaultValue,
  name,
  id = uniqueId(name),
  markers,
  max,
  maxPhrase,
  min,
  minPhrase,
  onBlur,
  onChange,
  onFocus,
  onMouseDown,
  onMouseUp,
  onTouchEnd,
  onTouchStart,
  value,
  ...restProps
}) => {
  const [controlledValue, setControlledValue] = useState(defaultValue)
  const [active, setActive] = useState(false)
  const [focused, setFocused] = useState(false)

  const [firstMarkerParams, setFirstMarkerParams] = useState({ width: 0 })
  const [secondMarkerParams, setSecondMarkerParams] = useState({ width: 0 })

  const firstMarkerRef = useRef(null)
  const secondMarkerRef = useRef(null)

  const myValue = controlledValue || value
  const Caption = caption || DefaultCaption
  const numMin = parseFloat(min)
  const numMax = parseFloat(max)
  const numVal = parseFloat(myValue)
  const percentFromMax = (numMax - numVal) / (numMax - numMin)
  const percentFromMin = 1 - percentFromMax

  useEffect(() => {
    setFirstMarkerParams({
      width: firstMarkerRef.current?.offsetWidth,
    })
    setSecondMarkerParams({
      width: secondMarkerRef.current?.offsetWidth,
    })
  }, [])

  const renderMarkers = () => {
    if (isEmpty(markers)) return <React.Fragment />

    return markers.map((marker, index) => {
      // Distance in percentage of the marker's value from the left side of the slider
      const markerPercentFromMin =
        1 - (numMax - marker.value) / (numMax - numMin)

      // Assume we will only ever have 1 or 2 markers
      const ref = index === 0 ? firstMarkerRef : secondMarkerRef

      const markerContentWidth =
        index === 0 ? firstMarkerParams.width : secondMarkerParams.width

      const markerIsUp = marker.direction === 'up'

      // Assume we will only have 1 or 2 lines of text in top markers
      // If the direction of the marker is up and it is a single line of content - apply the top vars px
      // Etc..
      let markerContentTop

      if (markerIsUp) {
        markerContentTop = marker.multiline
          ? markerContentTopUpDirectionDoubleLine
          : markerContentTopUpDirectionSingleLine
      } else {
        markerContentTop = markerContentTopDownDirection
      }

      return (
        <div key={uniqueId(marker.value)}>
          <div
            style={{
              top: markerIsUp ? markerTopUpDirection : markerTopDownDirection,
              left: `calc(${markerPercentFromMin} * 100% - ${markerWidth} / 2)`,
            }}
            className={styles.marker}
          />
          <div
            ref={ref}
            className={styles.markerContent}
            style={{
              top: markerContentTop,
              left: `calc(${markerPercentFromMin} * 100% - ${
                markerContentWidth / 2
              }px)`,
            }}
          >
            {marker.content}
          </div>
        </div>
      )
    })
  }

  return (
    <div className={className}>
      <div className={styles.slider}>
        <div
          className={styles.progress}
          style={{
            right: `calc((${percentFromMax} * 100%)`,
          }}
        />
        <div
          className={styles.background}
          style={{
            left: `calc((${percentFromMin} * 100%)`,
          }}
        />
        {renderMarkers()}
        <svg
          viewBox='0 0 2 2'
          className={styles.svg}
          style={{
            left: `calc(${percentFromMin} * 100%)`,
          }}
        >
          <circle
            className={classnames(
              styles.outerCircle,
              active && styles.active,
              focused && styles.focus
            )}
            cx='1'
            cy='1'
            r='1'
          />
          <circle
            className={classnames(
              styles.innerCircle,
              focused && styles.focused
            )}
            cx='1'
            cy='1'
            r='0.65'
          />
        </svg>
        <input
          id={id}
          name={name}
          type='range'
          onTouchStart={e => {
            setActive(true)
            if (onTouchStart) onTouchStart(e)
          }}
          onTouchEnd={e => {
            setActive(false)
            if (onTouchEnd) onTouchEnd(e)
          }}
          onMouseDown={e => {
            setActive(true)
            if (onMouseDown) onMouseDown(e)
          }}
          onMouseUp={e => {
            setActive(false)
            if (onMouseUp) onMouseUp(e)
          }}
          onFocus={e => {
            !active && setFocused(true)
            if (onFocus) onFocus(e)
          }}
          onBlur={e => {
            setFocused(false)
            if (onBlur) onBlur(e)
          }}
          onChange={event => {
            const newValue = event.target.value
            if (value == null) {
              setControlledValue(newValue)
            }
            if (onChange) {
              onChange(event)
            }
          }}
          min={min}
          max={max}
          value={myValue}
          className={styles.input}
          aria-label={ariaLabel}
          {...restProps}
        />
        <Caption minPhrase={minPhrase} maxPhrase={maxPhrase} />
      </div>
    </div>
  )
}

DefaultCaption.propTypes = defaultCaptionPropTypes

Slider.propTypes = propTypes
Slider.defaultProps = defaultProps

export default Slider
